import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { userRequest } from "../utils/utils";
import {
  IApplicationResponse,
  ICountriesWithPosts,
  ICreateApplicationResponse,
  IManagerServicesByCountry,
  IMessagesListResponse,
  IPartnerCompaniesResponse,
  IUserCommentResponse,
} from "../types";

interface IApplicationState {
  application: ICreateApplicationResponse | null;
  managersApplicationsList: IApplicationResponse | null;
  otherManagersApplicationsList: IApplicationResponse | null;
  userMessage: IMessagesListResponse | null;
  loading: boolean;
  currentManagerPage: number;
  currentOtherManagerPage: number;
  cache: Record<string, ICreateApplicationResponse>;
  lang: "en" | "ru" | "ge";
  comment: IUserCommentResponse | null;
  activeTab: string;
  searchResults: IApplicationResponse | null;
  lastSearchValue: string;
  partnersCompanies: IPartnerCompaniesResponse[] | null;
  countries: ICountriesWithPosts[] | null;
  managerServices: IManagerServicesByCountry[] | null;
  managersActiveTab: string;
}

const initialState: IApplicationState = {
  application: null,
  managersApplicationsList: null,
  otherManagersApplicationsList: null,
  userMessage: null,
  loading: false,
  currentManagerPage: 1,
  currentOtherManagerPage: 1,
  cache: {},
  lang: "ru",
  comment: null,
  activeTab: "1",
  searchResults: null,
  lastSearchValue: "",
  partnersCompanies: null,
  countries: null,
  managerServices: null,
  managersActiveTab: "myApplications",
};

export const getManagersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>("manager/getManagersApplicationsList", async (page, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/dashboard/api/manager/applications/broker-applications/?page=${page}`
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const getOtherManagersApplicationsList = createAsyncThunk<
  IApplicationResponse,
  number,
  { rejectValue: { error: string } }
>(
  "manager/getOtherManagersApplicationsList",
  async (page, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/manager/applications/related-applications/?page=${page}`
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const searchApplications = createAsyncThunk<
  IApplicationResponse,
  { search_term: string; page: number },
  { rejectValue: { error: string } }
>(
  "applications/searchApplications",
  async ({ search_term, page }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/search/application/?page=${page}`,
        { search_term }
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const getManagerApplicationDetails = createAsyncThunk<
  ICreateApplicationResponse,
  number,
  { rejectValue: { error: string } }
>("manager/getManagersApplicationDetails", async (id, { rejectWithValue }) => {
  try {
    const { data } = await userRequest.get(
      `/dashboard/api/manager/applications/${id}/`
    );
    return data;
  } catch (error: any) {
    console.error(error);
    return rejectWithValue({
      error: error.message || "Something went wrong",
    });
  }
});

export const updateApplication = createAsyncThunk<
  ICreateApplicationResponse,
  { id: number; formData: FormData },
  { rejectValue: { error: string } }
>(
  "applications/updateApplication",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.patch(
        `/dashboard/api/multi-update-application/${id}/`,
        formData
      );
      return data;
    } catch (error) {
      console.log(error);
    }
  }
);

export const getManagerServicesByCountry = createAsyncThunk<
  IManagerServicesByCountry[],
  void,
  { rejectValue: { error: string } }
>(
  "applications/getManagerServicesByCountry",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.get(
        `/dashboard/api/services-by-country/`
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const filtrApplicationsByStatusesAndEmployees = createAsyncThunk<
  IApplicationResponse,
  { page: number; status: string; user_id?: number },
  { rejectValue: { error: string } }
>(
  "applications/filtrApplicationsByStatusesAndEmployees",
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/api/manager/applications/by-user-status/?page=${params.page}`,
        params
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const searchManagersApplications = createAsyncThunk<
  IApplicationResponse,
  { search_term: string; page: number },
  { rejectValue: { error: string } }
>(
  "applications/searchManagersApplications",
  async ({ search_term, page }, { rejectWithValue }) => {
    try {
      const { data } = await userRequest.post(
        `/dashboard/search/manager/?page=${page}`,
        { search_term }
      );
      return data;
    } catch (error: any) {
      console.error(error);
      return rejectWithValue({
        error: error.message || "Something went wrong",
      });
    }
  }
);

export const managerSlice = createSlice({
  name: "manager",
  initialState,
  reducers: {
    setCurrentManagerPageNumber: (state, action) => {
      state.currentManagerPage = action.payload;
    },
    setCurrentOtherManagerPageNumber: (state, action) => {
      state.currentOtherManagerPage = action.payload;
    },

    setLang(state, action) {
      state.lang = action.payload;
    },

    setLastSearchValue: (state, action) => {
      state.lastSearchValue = action.payload;
    },
    resetApplication: () => initialState,
    setManagersActiveTab: (state, action) => {
      state.managersActiveTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(getManagersApplicationsList.fulfilled, (state, action) => {
        state.managersApplicationsList = action.payload;
        state.loading = false;
      })

      .addCase(getOtherManagersApplicationsList.fulfilled, (state, action) => {
        state.otherManagersApplicationsList = action.payload;
        state.loading = false;
      })

      .addCase(searchApplications.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      })

      .addCase(getManagerApplicationDetails.fulfilled, (state, action) => {
        state.application = action.payload;
        state.cache = state.cache || {};
        state.cache[action.payload.id] = action.payload;
        state.loading = false;
      })

      .addCase(updateApplication.pending, (state, action) => {
        state.loading = true;
      })

      .addCase(updateApplication.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(getManagerServicesByCountry.fulfilled, (state, action) => {
        state.managerServices = action.payload;
      })
      .addCase(
        filtrApplicationsByStatusesAndEmployees.fulfilled,
        (state, action) => {
          state.managersApplicationsList = action.payload;
        }
      )
      .addCase(searchManagersApplications.fulfilled, (state, action) => {
        state.searchResults = action.payload;
      });
  },
});
export const {
  setCurrentManagerPageNumber,
  setCurrentOtherManagerPageNumber,
  setLang,
  setLastSearchValue,
  resetApplication,
  setManagersActiveTab,
} = managerSlice.actions;
export default managerSlice.reducer;
